import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTenant } from "./TenantContext";
import { useApi } from "../hooks/useApi";

// Espelha REGRAS em src/platform_v2/access.py. O backend confere cada endpoint de
// novo; aqui e so para nao mostrar o que a pessoa nao pode abrir.
export type Area =
  | "kpis" | "dashboards" | "relatorios" | "relatorios.reenviar" | "notificacoes"
  | "integracoes" | "usuarios" | "logs" | "catalogo_de_personas" | "sophia" | "configuracoes"
  // Estado e configuracao de vendas/compras. Mesma capability de usuarios e
  // logs: e o admin do tenant quem precisa ver por que um modulo esta apagado.
  | "modulos"
  // A equipe de vendas. O vendedor nao entra na plataforma — esta tela e
  // de quem gerencia, entao a capability e `sophia.team.compare`.
  | "vendedores"
  // Ver a equipe e uma coisa; liberar alguem no WhatsApp e outra — a
  // segunda faz uma pessoa real passar a ser atendida e a consumir a
  // chave do cliente, entao pede `admin.grant`.
  | "vendedores.gerenciar"
  // Separada de "configuracoes": aquela e so do time UpOwl e de leitura; esta o
  // cliente muda, e passa pela capability `admin.llm.configure`.
  | "configuracoes.llm";

export interface PersonaInfo {
  id: string; name: string; description: string; scope: string; capabilities: string[];
}

export interface AccessInfo {
  tenant: string;
  is_internal: boolean;
  personas: string[];
  capabilities: string[];
  scope: string;
  has_access: boolean;
  reason: string;
  areas: Record<Area, boolean>;
  /**
   * Os modulos que este cliente contratou (`config["modulos"]`).
   *
   * Vem junto do acesso para o menu esconder secoes inteiras sem uma segunda
   * chamada. Nao protege nada: cada endpoint confere de novo.
   */
  modulos: string[];
  persona_catalog?: PersonaInfo[];
}

interface Ctx {
  access: AccessInfo | null;
  loading: boolean;
  can: (area: Area) => boolean;
  temModulo: (modulo: string) => boolean;
}

const AccessCtx = createContext<Ctx>({
  access: null, loading: true, can: () => false, temModulo: () => false,
});

export function AccessProvider({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  const api = useApi();
  const q = useQuery({
    queryKey: ["access", tenant],
    queryFn: () => api.call<AccessInfo>("/api/access"),
    enabled: !!tenant,
    staleTime: 60_000,
    retry: 0,
  });
  const access = q.data ?? null;
  // enquanto nao sabe, nega: melhor um item aparecer um instante depois do que sumir
  const can = (area: Area) => !!access?.areas?.[area];
  // Mesma regra do `can`: enquanto nao sabe, nega. Um item que aparece um
  // instante depois e melhor do que um que some na cara de quem ja clicou.
  const temModulo = (modulo: string) => !!access?.modulos?.includes(modulo);
  return (
    <AccessCtx.Provider value={{ access, loading: !!tenant && q.isLoading, can, temModulo }}>
      {children}
    </AccessCtx.Provider>
  );
}

export function useAccess() {
  return useContext(AccessCtx);
}
